import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '../../../../services/auth';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useTimezone } from '../../../../contexts/TimezoneContext';
import TOTPLoginStep from '../../components/TOTPLoginStep/TOTPLoginStep.jsx';
import './Login.css';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { updateLanguage, syncUserLanguage } = useLanguage();
  const { syncUserTimezone } = useTimezone();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' or 'totp'
  const [tempToken, setTempToken] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('Attempting login with:', formData.username);

    try {
      const response = await authService.login(formData.username, formData.password);
      console.log('Login response:', response);
      
      // Check if TOTP is required
      if (response.requiresTOTP) {
        setTempToken(response.tempToken);
        setLoginStep('totp');
        setError('');
        return;
      }
      
      // Complete normal login flow
      handleLoginSuccess(response);
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || t('auth:login.login_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (response) => {
    // Sync user's language preference
    if (response.user && response.user.language) {
      syncUserLanguage(response.user);
    }
    
    // Sync user's timezone preference
    if (response.user && response.user.timezone) {
      syncUserTimezone(response.user);
    }
    
    // Update parent component's auth state
    if (onLogin) {
      onLogin();
    }
    
    // Navigate to the intended route or dashboard
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  };

  const handleTOTPSuccess = (response) => {
    handleLoginSuccess(response);
  };

  const handleTOTPBack = () => {
    setLoginStep('credentials');
    setTempToken('');
    setError('');
  };

  const handleTOTPError = (errorMessage) => {
    setError(errorMessage);
  };

  const changeLanguage = async (lng) => {
    try {
      await updateLanguage(lng);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form">
        {/* Language Switcher */}
        <div className="language-switcher">
          <button 
            onClick={() => changeLanguage('en')}
            className={`lang-button ${i18n.language === 'en' ? 'active' : ''}`}
          >
            EN
          </button>
          <button 
            onClick={() => changeLanguage('es')}
            className={`lang-button ${i18n.language === 'es' ? 'active' : ''}`}
          >
            ES
          </button>
        </div>

        {loginStep === 'credentials' ? (
          <>
            <h2 className="login-title">
              {t('auth:login.title')}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  {t('common:labels.username')}:
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t('auth:login.username_placeholder')}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('common:labels.password')}:
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t('auth:login.password_placeholder')}
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="submit-button"
              >
                {loading ? t('auth:login.logging_in') : t('auth:login.login_button')}
              </button>

              <div className="form-actions">
                <Link to="/forgot-password" className="forgot-password-link">
                  {t('auth:login.forgot_password')}
                </Link>
              </div>
            </form>
          </>
        ) : (
          <>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            <TOTPLoginStep
              tempToken={tempToken}
              onSuccess={handleTOTPSuccess}
              onBack={handleTOTPBack}
              onError={handleTOTPError}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Login;